import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ConfirmModal } from './ConfirmModal';
import { UserPlus, Shield, ScrollText, ChevronDown, ChevronUp, Search } from 'lucide-react';

const ROLE_OPTIONS = ['super_admin', 'support_manager', 'onboarding_officer', 'finance_auditor'];

interface StaffRow {
  admin_id: string;
  user_id: string;
  full_name: string;
  email: string;
  admin_role: string;
  is_active: boolean;
  created_at: string;
}

function RoleBadge({ role }: { role: string }) {
  const colors: Record<string, string> = {
    super_admin: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    support_manager: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    onboarding_officer: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    finance_auditor: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  };
  return (
    <span className={`px-2 py-1 rounded text-xs font-semibold capitalize ${colors[role] || 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'}`}>
      {role.replace(/_/g, ' ')}
    </span>
  );
}

function StaffTab() {
  const { currentRole } = useAuth();
  const isSuperAdmin = currentRole === 'super_admin';

  const [staff, setStaff] = useState<StaffRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [foundUser, setFoundUser] = useState<any>(null);
  const [searching, setSearching] = useState(false);
  const [newRole, setNewRole] = useState('support_manager');
  const [submitting, setSubmitting] = useState(false);

  const [confirmTarget, setConfirmTarget] = useState<{ row: StaffRow; action: 'deactivate' | 'activate' } | null>(null);

  const fetchStaff = async () => {
    const supabase = getSupabaseClient();
    if (!supabase) return;
    setLoading(true);
    const { data, error } = await supabase.rpc('admin_list_platform_staff');
    if (error) {
      toast.error(error.message || 'Failed to load staff');
    } else {
      setStaff(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  const handleFindUser = async () => {
    if (!searchEmail.trim()) return;
    const supabase = getSupabaseClient();
    if (!supabase) return;
    setSearching(true);
    const { data, error } = await supabase
      .from('users')
      .select('id, full_name, email')
      .eq('email', searchEmail.trim().toLowerCase())
      .maybeSingle();
    setSearching(false);
    if (error || !data) {
      toast.error('User is email se nahi mila');
      setFoundUser(null);
    } else {
      setFoundUser(data);
    }
  };

  const handleAddStaff = async () => {
    if (!foundUser) return;
    const supabase = getSupabaseClient();
    if (!supabase) return;
    setSubmitting(true);
    const { error } = await supabase.rpc('admin_add_platform_staff', {
      p_user_id: foundUser.id,
      p_admin_role: newRole,
    });
    if (error) {
      toast.error(error.message || 'Staff add nahi ho paaya');
    } else {
      toast.success('Staff member add ho gaya');
      setAddOpen(false);
      setSearchEmail('');
      setFoundUser(null);
      setNewRole('support_manager');
      fetchStaff();
    }
    setSubmitting(false);
  };

  const handleRoleChange = async (row: StaffRow, role: string) => {
    const supabase = getSupabaseClient();
    if (!supabase) return;
    const { error } = await supabase.rpc('admin_update_platform_staff', {
      p_admin_id: row.admin_id,
      p_admin_role: role,
    });
    if (error) {
      toast.error(error.message || 'Role update failed');
    } else {
      toast.success('Role updated');
      fetchStaff();
    }
  };

  const handleToggleActive = async () => {
    if (!confirmTarget) return;
    const { row, action } = confirmTarget;
    const supabase = getSupabaseClient();
    if (!supabase) return;
    setSubmitting(true);
    const { error } = await supabase.rpc('admin_update_platform_staff', {
      p_admin_id: row.admin_id,
      p_is_active: action === 'activate',
    });
    if (error) {
      toast.error(error.message);
    } else {
      toast.success(action === 'activate' ? 'Staff activate ho gaya' : 'Staff deactivate ho gaya');
      setConfirmTarget(null);
      fetchStaff();
    }
    setSubmitting(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500 dark:text-slate-400">Platform staff roster — {staff.length} members</p>
        {isSuperAdmin && (
          <Button onClick={() => setAddOpen(true)} className="bg-primary hover:bg-primary/90 text-white gap-2">
            <UserPlus size={16} /> Add Staff
          </Button>
        )}
      </div>

      <Card className="dark:bg-slate-900 dark:border-slate-800 border-none shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400 font-medium">
              <tr>
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Email</th>
                <th className="px-6 py-3">Role</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {loading && (
                <tr><td colSpan={5} className="px-6 py-8 text-center text-slate-500 dark:text-slate-400">Loading staff...</td></tr>
              )}
              {!loading && staff.length === 0 && (
                <tr><td colSpan={5} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">No platform staff found.</td></tr>
              )}
              {!loading && staff.map((row) => (
                <tr key={row.admin_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="px-6 py-3 font-semibold dark:text-white">{row.full_name || '—'}</td>
                  <td className="px-6 py-3 dark:text-slate-300">{row.email}</td>
                  <td className="px-6 py-3">
                    {isSuperAdmin ? (
                      <Select value={row.admin_role} onValueChange={(v) => handleRoleChange(row, v)}>
                        <SelectTrigger className="w-44 h-8 text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLE_OPTIONS.map((r) => (
                            <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, ' ')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <RoleBadge role={row.admin_role} />
                    )}
                  </td>
                  <td className="px-6 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${row.is_active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                      {row.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-right">
                    {isSuperAdmin && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="dark:border-slate-700 dark:text-white"
                        onClick={() => setConfirmTarget({ row, action: row.is_active ? 'deactivate' : 'activate' })}
                      >
                        {row.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <ConfirmModal
        open={addOpen}
        title="Add Platform Staff"
        confirmLabel={submitting ? 'Adding...' : 'Add Staff'}
        loading={submitting}
        onConfirm={handleAddStaff}
        onCancel={() => { setAddOpen(false); setFoundUser(null); setSearchEmail(''); }}
      >
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder="User email dhoondhein..."
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              className="dark:bg-slate-800 dark:border-slate-700 dark:text-white"
            />
            <Button variant="outline" onClick={handleFindUser} disabled={searching} className="dark:border-slate-700 dark:text-white gap-1">
              <Search size={14} /> Find
            </Button>
          </div>
          {foundUser && (
            <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 rounded-lg text-sm">
              <p className="font-semibold dark:text-white">{foundUser.full_name || 'Unnamed'}</p>
              <p className="text-slate-500 dark:text-slate-400">{foundUser.email}</p>
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-sm font-medium dark:text-slate-300">Role</label>
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger className="dark:bg-slate-800 dark:border-slate-700 dark:text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((r) => (
                  <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </ConfirmModal>

      <ConfirmModal
        open={!!confirmTarget}
        title={confirmTarget?.action === 'deactivate' ? 'Deactivate this staff member?' : 'Activate this staff member?'}
        description={confirmTarget ? `${confirmTarget.row.full_name} (${confirmTarget.row.email})` : ''}
        destructive={confirmTarget?.action === 'deactivate'}
        loading={submitting}
        onConfirm={handleToggleActive}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
}

function AuditLogsTab() {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const [actionTypes, setActionTypes] = useState<string[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;

  const fetchLogs = async () => {
    const supabase = getSupabaseClient();
    if (!supabase) return;
    setLoading(true);
    let query = supabase
      .from('audit_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

    if (actionFilter !== 'all') query = query.eq('action_type', actionFilter);

    const { data, error } = await query;
    if (!error && data) {
      setLogs(data);
      if (actionTypes.length === 0) {
        const { data: allTypes } = await supabase.from('audit_logs').select('action_type');
        if (allTypes) {
          const types = Array.from(new Set(allTypes.map((r: any) => r.action_type as string))).sort() as string[];
          setActionTypes(types);
        }
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionFilter, page]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <p className="text-sm text-slate-500 dark:text-slate-400">System-wide admin action history</p>
        <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(0); }}>
          <SelectTrigger className="w-56 dark:bg-slate-800 dark:border-slate-700 dark:text-white">
            <SelectValue placeholder="Filter by action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            {actionTypes.map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="dark:bg-slate-900 dark:border-slate-800 border-none shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400 font-medium">
              <tr>
                <th className="px-6 py-3">Actor</th>
                <th className="px-6 py-3">Action</th>
                <th className="px-6 py-3">Table</th>
                <th className="px-6 py-3">Target</th>
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {loading && (
                <tr><td colSpan={6} className="px-6 py-8 text-center text-slate-500 dark:text-slate-400">Loading audit logs...</td></tr>
              )}
              {!loading && logs.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">No audit logs found.</td></tr>
              )}
              {!loading && logs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer" onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}>
                    <td className="px-6 py-3 font-mono text-xs dark:text-slate-300">{log.actor_user_id?.substring(0, 8) || 'system'}...</td>
                    <td className="px-6 py-3 font-medium dark:text-white">{log.action_type}</td>
                    <td className="px-6 py-3 dark:text-slate-300">{log.target_table || '-'}</td>
                    <td className="px-6 py-3 font-mono text-xs dark:text-slate-400">{log.target_id ? `${String(log.target_id).substring(0, 8)}...` : '-'}</td>
                    <td className="px-6 py-3 text-slate-500 dark:text-slate-400">{new Date(log.created_at).toLocaleString()}</td>
                    <td className="px-6 py-3 text-right">
                      {expandedId === log.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </td>
                  </tr>
                  {expandedId === log.id && (
                    <tr className="bg-slate-50 dark:bg-slate-800/30">
                      <td colSpan={6} className="px-6 py-3">
                        <pre className="text-xs whitespace-pre-wrap dark:text-slate-300 bg-white dark:bg-slate-950 p-3 rounded border dark:border-slate-800 overflow-x-auto">
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-between items-center p-3 border-t dark:border-slate-800">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="dark:border-slate-700 dark:text-white">
            Previous
          </Button>
          <span className="text-xs text-slate-400">Page {page + 1}</span>
          <Button variant="outline" size="sm" disabled={logs.length < PAGE_SIZE} onClick={() => setPage((p) => p + 1)} className="dark:border-slate-700 dark:text-white">
            Next
          </Button>
        </div>
      </Card>
    </div>
  );
}

export function StaffSecurityView() {
  const [tab, setTab] = useState<'staff' | 'audit'>('staff');

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold dark:text-white">Staff & Security</h1>
          <p className="text-slate-500 dark:text-slate-400">Manage platform admins and review the full audit trail.</p>
        </div>
        <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
          <Button variant={tab === 'staff' ? 'default' : 'ghost'} size="sm" onClick={() => setTab('staff')} className="gap-1">
            <Shield size={14} /> Staff
          </Button>
          <Button variant={tab === 'audit' ? 'default' : 'ghost'} size="sm" onClick={() => setTab('audit')} className="gap-1">
            <ScrollText size={14} /> Audit Logs
          </Button>
        </div>
      </div>

      {tab === 'staff' ? <StaffTab /> : <AuditLogsTab />}
    </div>
  );
}

export default StaffSecurityView;
